(ns app.front.view.meeting.meeting-core
  (:require [app.front.ui :as ui]
            [app.front.ui.chevron :as c]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.unsaved-changes :as unsaved-changes]
            [app.front.util :as futil]
            [app.front.view.meeting.meeting-events :as meeting-events]
            [app.front.view.meeting.meeting-subs :as meeting-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [potpuri.core :as p]))


(defn- draft? [meeting]
  (= :meeting.state/draft (:domain/state meeting)))


(defn- required-fields-missing? [{:meeting/keys [topic] :keys [start end date]}]
  (boolean (or (nil? topic)
               (nil? date)
               (some nil? start)
               (some nil? end))))


(defn- field-ids [meeting-id]
  {:meeting/topic (str "topic-" meeting-id)
   :date          (str "date-" meeting-id)
   :start         (str "start-" meeting-id)
   :end           (str "end-" meeting-id)
   :meeting/notes (str "notes-" meeting-id)})


(defn- editing-permitted? [parent]
  (<sub [:app.front.process/signal-permitted?
         (:db/id parent)
         [:process.signal/edit-meeting]]))


(defn- read-only-field [tr label-loc value]
  [:div.mb-2.mr-5
   [:label.bx--label.block (tr [label-loc])]
   value])


(defn- date-field [{:keys [id tr parent meeting class]}]
  (if (editing-permitted? parent)
    [unsaved-changes/wrapper
     {:id            id
      :on-change-key :callback}
     ui/date-picker
     {:tr          tr
      :label-loc   :meeting/date
      :class-name  class
      :class-input "bg-white"
      :required?   true
      :value       (some-> meeting :date)}]
    [read-only-field tr :meeting/date (date-util/date-in-tz (:date meeting) date-util/browser-timezone)]))


(defn- time-field [{:keys [id tr parent meeting text-loc field] :as opts}]
  (if (editing-permitted? parent)
    [unsaved-changes/wrapper
     {:id id}
     ui/time-input
     (merge (dissoc opts :tr :meeting :text-loc :field :parent)
            {:id         id
             :light?     true
             :label-text (tr [text-loc])
             :required?  true
             :value      (get meeting field [nil nil])})]
    [read-only-field tr text-loc (if (= :end field)
                                   (:ends-at meeting)
                                   (:starts-at meeting))]))


(defn- topic-field [{:keys [id tr parent meeting]}]
  (when (editing-permitted? parent)
    [unsaved-changes/wrapper
     {:id     id
      :raw-fn futil/tval
      :sync?  true}
     ui/text-input
     {:label-text (tr [:meeting/topic])
      :data-cy    id
      :id         (str "meeting-topic-input-" id)
      :light      true
      :required   true
      :value      (:meeting/topic meeting)}]))


(defn- notes-field [{:keys [id tr parent] {:meeting/keys [notes] :as meeting} :meeting}]
  (let [auth-group (-> parent
                       :entity/parent ; Workspace
                       :domain/authority-group)]
    (if (editing-permitted? parent)
      [unsaved-changes/wrapper
       {:id        id
        :value-key :initial-content}
       ui-rt/editor-legacy
       {:id                      (str "meeting-notes-" (:db/id meeting))
        :label-text              (tr [:meeting/notes])
        :initial-value           notes
        :default-phrase-category (-> auth-group
                                     :group/phrase-category-inspections)
        :authority-group-id      (:db/id auth-group)}]
      [ui-rt/editor-legacy {:id            (str "meeting-notes-" (:db/id meeting))
                            :label-text    (tr [:meeting/notes])
                            :initial-value notes}])))


(defn- meeting-buttons [{tr :tr {meeting-id :db/id :as meeting} :meeting {parent-id :db/id} :parent}]
  (let [fields  (field-ids meeting-id)
        changes (->> fields
                     (p/map-vals #(<sub [::unsaved-changes/unsaved-value %]))
                     (p/filter-vals some?)
                     (merge meeting))]
    (if (<sub [::unsaved-changes/unsaved-values? (vals fields)])
      [:<>
       ;; Cancel or save changes
       (cc/button {:kind       :tertiary
                   :data-cy    "cancel-changes-btn"
                   :class-name "mr-2"
                   :on-click   #(>evt [::meeting-events/cancel-changes (vals fields)])}
                  (tr [:cancel-changes]))
       (cc/button {:kind     :primary
                   :data-cy  "save-changes-btn"
                   :disabled (required-fields-missing? changes)
                   :on-click #(>evt [::meeting-events/save-changes parent-id changes (vals fields)])}
                  (tr [:save-changes]))]
      [:<>
       ;; Cancel meeting
       [ui/modal-confirmation-button
        {:trigger-button-opts {:kind       :tertiary
                               :icon       i/trash
                               :class-name "mr-2"}
         :trigger-button-text (tr [:meeting/cancel-meeting])
         :on-yes-click        #(>evt [::meeting-events/cancel-meeting parent-id meeting-id])
         :tr                  tr
         :id                  "cancel-meeting"}
        (tr [:meeting/cancel-confirmation])]

       ;; Publish draft
       (when (= :meeting.state/draft (:domain/state meeting))
         (cc/button {:kind     :primary
                     :disabled (required-fields-missing? meeting)
                     :on-click #(>evt [::meeting-events/publish parent-id meeting-id])}
                    (tr [:publish])))

       ;; Mark as held
       (when (= :meeting.state/published (:domain/state meeting))
         (cc/button {:kind     :primary
                     :id       "mark-as-held-btn"
                     :on-click #(>evt [::meeting-events/mark-as-held parent-id meeting-id])
                     :data-cy  (str "mark-as-held-" meeting-id)}
                    (tr [:meeting/mark-as-held])))])))


(defn- meeting-header [{{:meeting/keys [topic]
                         :keys         [starts-at date latest-change]
                         id            :db/id
                         :as           meeting}                       :meeting
                        tr                                            :tr}]
  (let [expanded? (<sub [::meeting-subs/expanded? id])]
    [:div.p-3.w-full.flex.items-center.rounded-t
     {:class [(when-not (draft? meeting) "bg-ui-light-gray")
              (when-not expanded? "rounded-b")]}
     [:button.px-2.border-0.bg-transparent.flex-initial
      {:on-click #(>evt [::meeting-events/toggle-expanded id])}
      [c/chevron {:tr        tr
                  :size      16
                  :side      :left
                  :fill      "var(--ui-night-sky)"
                  :icon      i/chevron-right
                  :expanded? expanded?}]]
     [:div.px-3.flex-col.flex-1
      [:div.flex-initial.bold (if (str/blank? topic) (tr [:meeting/new-meeting]) topic)]
      [:div.flex-initial
       (when (some? latest-change)
         [:span.text-gray-800.pt-2
          (tr [:last-change])
          " "
          (tr [:last-change-by] [((<sub [::date-util/date-time-fn]) (:ts latest-change)) (:user latest-change)])])]]
     [:div.flex-col
      (when (some? starts-at)
        [:div.flex-initial
         [:span.mr-3 (date-util/date-in-tz date date-util/browser-timezone)]
         [:span starts-at]])
      (when (draft? meeting)
        [:span.font-bold
         (tr [:meeting/draft])])]]))


(defn meeting-view
  "Component for displaying and editing meeting information.
  Props:
  `:tr`      tr
  `:meeting` entity (considered a draft if missing `:db/id`)"
  [{:keys [parent] {id :db/id :as meeting} :meeting :as opts}]
  (let [expanded?   (<sub [::meeting-subs/expanded? id])
        refresh-key (<sub [::unsaved-changes/reset-counter])]
    [eb/error-boundary
     [:div.my-5.rounded
      {:class   (cond
                  (not (draft? meeting))
                  "border border-solid border-ui-gray bg-ui-white"

                  expanded?
                  "border-2 border-solid border-ui-blue-100 bg-ui-blue-10"

                  :else
                  "border border-dashed border-ui-gray bg-ui-white")
       :data-cy "single-meeting"}
      [meeting-header opts]
      (when expanded?
        [:div.p-4
         [:div.bx--row.pt-1
          [:div.bx--col-xs-12.flex.flex-wrap
           [date-field (assoc opts :id (str "date-" id) :class "mr-5 mt-2")]
           [time-field (assoc opts :text-loc :meeting/starts-at :field :start :id (str "start-" id) :class "mr-5 mt-2")]
           [time-field (assoc opts :text-loc :meeting/ends-at :field :end :id (str "end-" id) :class "mt-2")]]]
         (when (editing-permitted? parent)
           [:div.bx--row.pt-3
            [:div.bx--col-xs-12 [topic-field (assoc opts :id (str "topic-" id))]]])
         [:div.bx--row.pt-3
          [:div.bx--col-xs-12 [notes-field (assoc opts :key (str "notes-" id refresh-key) :id (str "notes-" id))]]]
         (when (editing-permitted? parent)
           [:div.bx--row.pt-3
            [:div.bx--col-xs-12.flex.justify-end
             [meeting-buttons opts]]])])]]))
